import moment from 'moment';
/* TODO - come up with a better way of handling this
  Moment locales (loaded directly here so it's accessible across the app)
  lazy loading is too slow. The component render before the dynamic import
  completes so shows dates in fallback 'en'
*/
import 'moment/locale/en-gb';
import 'moment/locale/fr';

import { STORAGE_KEYS, getLocalStorage } from './storage';
import { DEFAULT_BACKUP_CULTURE } from './culture';

/**
 * Return 'fr' if 'fr-fr' from BE pattern to match moment locales,
 * but still accept 'en-gb' for example.
 * @param {string} locale - The locale string from the backend.
 * @returns {string} The normalised locale string.
 */
function normaliseLocale(locale) {
  const parts = locale.split('-');
  if (parts[0] === parts[1]) {
    return parts[0];
  }
  return locale.replace('_', '-'); // just in case of odd data from backend using underscore.
}

/**
 * Sets the global locale for Moment.js dynamically by importing the necessary locale data.
 * If the locale data fails to load, it falls back to the default backup culture.
 *
 * @param {string} locale - The locale to be set for Moment.js.
 */
export function setMomentLocaleGlobally(locale) {
  const normalisedLocale = normaliseLocale(locale);
  try {
    // If we've already imported locale, set and forget
    if (moment.locales().includes(normalisedLocale)) {
      moment.locale(normalisedLocale);
      return;
    }
    // Otherwise we need to import it
    import(`moment/locale/${normalisedLocale}`)
      .then(() => {
        moment.locale(normalisedLocale);
      })
      .catch((err) => {
        console.error(`Failed to load the locale: ${normalisedLocale}`, err);
        moment.locale(DEFAULT_BACKUP_CULTURE);
      });
  } catch (err) {
    console.error('Error importing the locale dynamically', err);
    moment.locale(DEFAULT_BACKUP_CULTURE);
  }
}

/**
 * Retrieves the current culture from local storage or falls back to the default culture.
 *
 * @returns {string} The current culture identifier.
 */
export function getCurrentCulture() {
  const currentCulture = getLocalStorage(STORAGE_KEYS.currentCulture, DEFAULT_BACKUP_CULTURE);
  return currentCulture;
}

/**
 * Initializes the locale setting for Moment.js using the current culture setting from local storage.
 * This function ensures that the correct locale is used globally across all date operations.
 */
export function initialiseCurrentCulture() {
  const currentCulture = getCurrentCulture();
  setMomentLocaleGlobally(currentCulture);
}
